/**
 * Academy domain — `AcademyLiveEngagementStore` state shape. Remote-only, in-memory.
 * Tracks the viewer's per-event engagement: subscription, rating, VOD session and
 * monotonic VOD/resume positions. Subscribe/rate ops land in US6 over this same shape.
 */
import type { Guid } from './academy-enums.model';
import type { AttendanceItem } from './academy-responses.model';
import type {
  LiveRatingResponse,
  LiveSubscriptionResponse,
} from './academy.model';

export interface AcademyLiveEngagementState {
  /** Viewer's subscription per event (`null` once known to be absent). */
  subscriptionByEvent: Record<Guid, LiveSubscriptionResponse | null>;
  /** Viewer's rating per event (`null` once known to be absent). */
  ratingByEvent: Record<Guid, LiveRatingResponse | null>;
  /** Resume position (seconds) per event — max over sessions. */
  resumeByEvent: Record<Guid, number>;
  /** Open VOD session flag per event. */
  vodSessionByEvent: Record<Guid, boolean>;
  /** Monotonic max of reported watched seconds per event (never regresses). */
  vodProgressByEvent: Record<Guid, number>;
  /**
   * Producer attendance roster per event — one page of `GET /LiveEvents/{id}/attendances`
   * (cross-tenant, open sessions first). Live `ParticipantJoined/Left` events are merged
   * over it by `AcademyLivePanelStore`.
   */
  attendancesByEvent: Record<Guid, AttendanceItem[]>;
  /** Server `Total` of that roster (sessions), which can exceed the loaded page. */
  attendancesTotalByEvent: Record<Guid, number>;
}

export const initialAcademyLiveEngagementState: AcademyLiveEngagementState = {
  subscriptionByEvent: {},
  ratingByEvent: {},
  resumeByEvent: {},
  vodSessionByEvent: {},
  vodProgressByEvent: {},
  attendancesByEvent: {},
  attendancesTotalByEvent: {},
};
